import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { reportQueries, toUiDetail, toUiSteps } from "../../../queries";
import { reportMutations } from "../../../mutations";
import type { UiReportDetail, UiReportStepRow } from "../../../queries/reports";
import { useUpdateReportStep } from "./reportstep/use-reportstep-update";
import { useDeleteReportStep } from "./reportstep/use-reportstep-delete";
import { useUpdateReportMetadata } from "./use-report-setting-update";
import { useRestartReportStep } from "./reportstep/use-reportstep-restart";
import { useRecordPermissions } from "@/domains/tenant/hooks";

interface UseReportDetailsReturn {
  report: UiReportDetail | undefined;
  steps: UiReportStepRow[] | undefined;
  isLoading: boolean;
  error: Error | null;
  editingId: string | null;
  draft: string;
  startingId: string | null;
  isRestarting: boolean;
  isExporting: boolean;
  isDeleting: boolean;
  isReadOnly: boolean;
  executionError: string | null;
  onEdit: (step: UiReportStepRow) => void;
  onSave: (step: UiReportStepRow) => void;
  onCancel: () => void;
  onDraftChange: (draft: string) => void;
  onRestart: () => Promise<void>;
  onRestartStep: (stepId: string) => Promise<void>;
  onDelete: (step: UiReportStepRow) => void;
  onDeleteReport: () => void;
  onUpdateMetadata: (values: {
    displayName: string;
    context: string;
  }) => Promise<void>;
  onExport: () => Promise<void>;
}

export function useReportDetails(
  uuid: string | undefined,
): UseReportDetailsReturn {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updateStep = useUpdateReportStep();
  const deleteStep = useDeleteReportStep();
  const updateMetadata = useUpdateReportMetadata();
  const restartStep = useRestartReportStep();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [startingId, setStartingId] = useState<string | null>(null);
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data: reportRuntime,
    isLoading: isLoadingReportRuntime,
    error: reportRuntimeError,
  } = useQuery(
    reportQueries.detail(
      uuid ?? "",
      !editingId && !deleteStep.isPending && !updateMetadata.isPending,
    ),
  );
  const {
    data: stepRuntime,
    isLoading: isLoadingStepRuntime,
    error: stepRuntimeError,
  } = useQuery(
    reportQueries.steps(
      uuid ?? "",
      !editingId && !deleteStep.isPending && !updateMetadata.isPending,
    ),
  );
  const {
    data: reportPresentation,
    isLoading: isLoadingReportPresentation,
    error: reportPresentationError,
  } = useQuery({
    ...reportQueries.detailPresentation(uuid ?? "", reportRuntime?.template_id),
    enabled: Boolean(uuid && reportRuntime),
  });
  const {
    data: stepPresentation,
    isLoading: isLoadingStepPresentation,
    error: stepPresentationError,
  } = useQuery({
    ...reportQueries.stepPresentation(uuid ?? "", stepRuntime ?? []),
    enabled: Boolean(uuid && stepRuntime),
  });

  const report =
    reportRuntime && reportPresentation
      ? toUiDetail(reportRuntime, reportPresentation)
      : undefined;
  const steps =
    stepRuntime && stepPresentation
      ? toUiSteps(stepRuntime, stepPresentation)
      : undefined;
  const { canEdit } = useRecordPermissions();
  const isLoading =
    isLoadingReportRuntime ||
    isLoadingStepRuntime ||
    isLoadingReportPresentation ||
    isLoadingStepPresentation;
  const error =
    reportRuntimeError ??
    stepRuntimeError ??
    reportPresentationError ??
    stepPresentationError;
  const isReadOnly = !canEdit(report?.record_tenant_id);

  function onEdit(step: UiReportStepRow) {
    if (isReadOnly) return;
    setEditingId(step.analysis_step_id);
    setDraft(step.value ?? "");
    updateStep.reset();
  }

  function onSave(step: UiReportStepRow) {
    if (isReadOnly) return;
    updateStep.mutate(
      {
        stepId: step.analysis_step_id,
        value: draft,
        reportId: uuid ?? "",
      },
      {
        onSuccess: () => {
          setEditingId(null);
          setDraft("");
        },
      },
    );
  }

  function onCancel() {
    setEditingId(null);
    setDraft("");
    updateStep.reset();
  }

  async function onRestart() {
    if (!uuid || isReadOnly) return;
    setExecutionError(null);
    setIsRestarting(true);
    try {
      const response = await reportMutations.analysisStep.restartAnalysis(uuid);
      if (response.error) throw response.error;
      await queryClient.invalidateQueries(reportQueries.detail(uuid));
      await queryClient.invalidateQueries(reportQueries.steps(uuid));
    } catch (error) {
      setExecutionError(
        error instanceof Error ? error.message : "Failed to restart report",
      );
    } finally {
      setIsRestarting(false);
    }
  }

  async function onRestartStep(stepId: string) {
    if (!uuid || isReadOnly) return;
    setExecutionError(null);
    setStartingId(stepId);
    try {
      await restartStep.mutateAsync({ reportId: uuid, stepId });
    } catch (error) {
      setExecutionError(
        error instanceof Error
          ? error.message
          : "Failed to restart report step",
      );
    } finally {
      setStartingId(null);
    }
  }

  function onDelete(step: UiReportStepRow) {
    if (!uuid || isReadOnly) return;
    deleteStep.mutate({ stepId: step.analysis_step_id, reportId: uuid });
  }

  async function onUpdateMetadata(values: {
    displayName: string;
    context: string;
  }) {
    if (!uuid || isReadOnly) return;
    await updateMetadata.mutateAsync({ reportId: uuid, ...values });
  }

  async function onExport() {
    if (!uuid) return;
    setIsExporting(true);
    try {
      const response = await reportMutations.analysisExport.exportPdf(uuid);
      if (response.error) throw response.error;
      const fileUrl = response.data?.file_url;
      if (fileUrl) window.open(fileUrl, "_blank");
    } catch (error) {
      console.error("Failed to export PDF:", error);
    } finally {
      setIsExporting(false);
    }
  }

  async function handleDeleteReport() {
    if (!uuid || isReadOnly) return;
    setIsDeleting(true);
    try {
      const { error } = await reportMutations.analysisDelete.delete(uuid);
      if (error) throw error;
      await queryClient.invalidateQueries(reportQueries.lists());
      void navigate("/reports");
    } catch (error) {
      console.error("Failed to delete report:", error);
    } finally {
      setIsDeleting(false);
    }
  }

  return {
    executionError,
    report,
    steps,
    isLoading,
    error,
    editingId,
    draft,
    startingId,
    isRestarting,
    isExporting,
    isDeleting,
    isReadOnly,
    onEdit,
    onSave,
    onCancel,
    onDraftChange: setDraft,
    onRestart,
    onRestartStep,
    onDelete,
    onDeleteReport: () => void handleDeleteReport(),
    onUpdateMetadata,
    onExport,
  };
}
